import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {inject} from '@angular/core';
import {
  CreateProductOptionGroupDto,
  ProductOptionGroupClient,
  UpdateGroupItemsAvailabilityDto,
  UpdateProductOptionGroupDto,
  UpdateProductOptionGroupItemsDto
} from '../../core/services/service-clients';
import {MessageService} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {ProductOptionGroupState} from './models/product-option-group.model';

const initialState: ProductOptionGroupState = {
  productOptionGroups: [],
  loading: false,
};

export const productOptionGroupStore = signalStore(
  withState<ProductOptionGroupState>(initialState),

  withMethods(store => {
    const productOptionGroupClient = inject(ProductOptionGroupClient);
    const messageService = inject(MessageService);

    return {
      // Load all product option groups
      async loadProductOptionGroups() {
        patchState(store, {loading: true});
        try {
          const productOptionGroups = await firstValueFrom(productOptionGroupClient.getAll());
          patchState(store, {productOptionGroups, loading: false});
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Laden der Produktoptionsgruppen',
          });
          patchState(store, {loading: false});
        }
      },

      // Create a new product option group
      async createProductOptionGroup(group: CreateProductOptionGroupDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(productOptionGroupClient.create(group));
          await this.loadProductOptionGroups();
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Gruppe erfolgreich erstellt',
          });
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Erstellen der Gruppe',
          });
          patchState(store, {loading: false});
        }
      },

      // Update a product option group
      async updateProductOptionGroup(groupId: number, group: UpdateProductOptionGroupDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(productOptionGroupClient.update(groupId, group));
          await this.loadProductOptionGroups();
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Gruppe erfolgreich aktualisiert',
          });
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Aktualisieren der Gruppe',
          });
          patchState(store, {loading: false});
        }
      },

      // Delete a product option group
      async deleteProductOptionGroup(groupId: number) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(productOptionGroupClient.delete(groupId));
          await this.loadProductOptionGroups();
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Gruppe erfolgreich gelöscht',
          });
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Löschen der Gruppe',
          });
          patchState(store, {loading: false});
        }
      },

      // Update group items (products and options)
      async updateGroupItems(groupId: number, items: UpdateProductOptionGroupItemsDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(productOptionGroupClient.updateItems(groupId, items));
          await this.loadProductOptionGroups();
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Gruppenitems erfolgreich aktualisiert',
          });
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Aktualisieren der Gruppenitems',
          });
          patchState(store, {loading: false});
        }
      },

      // Update group items availability (mark as available/unavailable)
      async updateGroupAvailability(groupId: number, available: boolean) {
        patchState(store, {loading: true});
        try {
          const dto = new UpdateGroupItemsAvailabilityDto({
            available: available
          });
          await firstValueFrom(productOptionGroupClient.updateAvailability(groupId, dto));
          await this.loadProductOptionGroups();
          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: `Produkte und Optionen erfolgreich als ${available ? 'verfügbar' : 'nicht verfügbar'} markiert`,
          });
        } catch {
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Fehler beim Aktualisieren der Verfügbarkeit',
          });
          patchState(store, {loading: false});
        }
      }
    };
  })
);
